import { createSelector } from 'reselect'

import { openModalDescriptorSelector } from '../modals/selectors'
import { featureValueSelector } from '../features/selectors'
import { EditingUsers } from './types'
import { RootState } from '../../reducers'
import { isModalAnExclusiveUi } from './utils'

export const socketServerSliceSelector = (state: RootState) => state.socketServer
export function isUnsavedSelector(state: RootState): boolean {
  if (isBotStateOutdatedSelector(state)) {
    return false
  }

  return !!state.socketServer.roomState?.botState?.diff
}

export const isSocketServerConnectedSelector = (state: RootState) => state.socketServer.isConnected
export const roomStateSelector = (state: RootState) => state?.socketServer?.roomState

export const mySocketIdSelector = (state: RootState) => state.socketServer.socketId

export const lastVersionStringForCurrentBotSelector = (state: RootState) =>
  roomStateSelector(state)?.lastVersion

export function hasRoomStateSelector(state: RootState): boolean {
  return !!roomStateSelector(state)
}

export function isDeployingSelector(state: RootState): boolean {
  return (
    !!roomStateSelector(state)?.isDeploying ||
    !!state.app.present.deploying ||
    !!state.deployBot.deploying
  )
}

export function hostingErrorSelector(state: RootState): string | undefined {
  return roomStateSelector(state)?.hostingError
}

export const exclusiveUiUserSelector = (state: RootState) =>
  roomStateSelector(state)?.exclusiveUiUser ?? null

export function botDiffSelector({ socketServer }: RootState): string {
  return socketServer.roomState?.botState?.diff || ''
}

export function botTesterSocketStateSelector(state: RootState) {
  return roomStateSelector(state)?.botState?.botTester ?? { runningTests: [] }
}

export function kbRunsSocketStateSelector(state: RootState) {
  return roomStateSelector(state)?.kbIndexing ?? { activeRuns: {} }
}

export function lastUpdatedSelector({ socketServer }: RootState): number {
  return socketServer.roomState?.botState.lastUpdated || 0
}

export const doIHaveExclusiveUiOpenSelector = createSelector(
  openModalDescriptorSelector,
  openModalDescriptor => !!openModalDescriptor && isModalAnExclusiveUi(openModalDescriptor)
)

export const amIBlockedByExclusiveUiSelector = createSelector(
  [
    exclusiveUiUserSelector,
    mySocketIdSelector,
    state => featureValueSelector('CROSS_USER_UI_BLOCKING')(state)
  ],
  (exclusiveUiUser, mySocketId, isUiBlockingEnabled) => {
    if (!isUiBlockingEnabled) {
      return false
    }

    if (!exclusiveUiUser) {
      return false
    }

    return exclusiveUiUser.socketId !== mySocketId
  }
)

export function editingUsersSelector(state: Pick<RootState, 'socketServer'>): EditingUsers {
  return state.socketServer.roomState?.botState?.editingUsers || {}
}

export const connectedUsersSelector = (state: RootState) => {
  return state.socketServer.roomState?.connectedUsers || {}
}

export const nameOfExclusiveUiUserSelector = createSelector(
  [exclusiveUiUserSelector, connectedUsersSelector],
  (exclusiveUiUser, connectedUsers) => {
    return Object.values(connectedUsers).find(user => user?.id === exclusiveUiUser?.userId!)?.name
  }
)

export function isMigratingMediaGallerySelector(state: RootState): boolean {
  return !!roomStateSelector(state)?.isMigratingMediaGallery
}

export function isBotStateOutdatedSelector(state: RootState) {
  return !socketServerSliceSelector(state).isBotStateUpToDate
}
